import {
  Banner as DixaBanner,
  Box,
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  InputSelectionList,
  ModalBody,
  ModalHead,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { useDebounce } from 'lib-knowledge-delivery-common/hooks/useDebounce';
import {
  FieldInputText,
  FieldInputTextArea,
  FieldTogglePanel,
  getFieldMeta,
} from 'lib-knowledge-form';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Waypoint } from 'react-waypoint';

import { useSearchFetch } from 'hooks/useSearchFetch';

import { CollectionDetachModal } from '../collectionDetachModal';
import { CollectionModalForm } from '../collectionEditModal';

type CollectionModalBodyProps = {
  closeModal: () => void;
  loading: boolean | undefined;
  control: Control<CollectionModalForm>;
  onSave: () => void;
  disabled: boolean;
  title: string;
  isValid: boolean;
  collectionId?: string;
  isSyncedWithElevio?: boolean;
};

export const CollectionModalBody = ({
  closeModal,
  control,
  onSave,
  disabled,
  title,
  collectionId,
  isSyncedWithElevio,
  isValid,
}: CollectionModalBodyProps): JSX.Element => {
  const isEdit = !!collectionId;
  const [queueSearchValue, setQueueSearchValue] = useState('');
  const queueSearch = useDebounce(queueSearchValue, 500);

  const { data, error, hasNextPage, isFetching, isLoading, nextPage } =
    useSearchFetch(
      {
        scope: 'queues',
        query: queueSearch,
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'is_archived',
              operator: 'not_equals',
              values: [{ value: 'true' }],
            },
          ],
        },
        sortBy: { field: 'name', order: 'asc' },
      },
      { skip: !isEdit },
    );
  // Only block the input while the initial (unfiltered) list loads, so typing
  // a search doesn't disable the field and steal focus on every refetch.
  const isLoadingQueues = isLoading && !queueSearch;

  return (
    <ModalWrapper>
      <ModalHead title={title} onClose={closeModal}></ModalHead>
      <ModalBody>
        {collectionId && isSyncedWithElevio && (
          <FormField>
            <CollectionDetachBanner
              collectionId={collectionId}
              isSyncedWithElevio={isSyncedWithElevio}
            />
          </FormField>
        )}
        <FormField>
          <FieldInputText
            label="Name"
            control={control}
            name="collectionTitle"
            required={true}
            disabled={disabled}
          />
        </FormField>
        {isEdit && (
          <>
            <FormField>
              <FieldInputTextArea
                control={control}
                name="description"
                label="Description"
                height={88}
                width="100%"
                disabled={disabled}
              />
            </FormField>
            <FormField>
              <Controller
                control={control}
                name="linkedQueues"
                render={({ field, fieldState }) => (
                  <FieldWrapper label="Queues" meta={getFieldMeta(fieldState)}>
                    <InputSelectionList<{ id: string; name: string }>
                      multiple
                      value={field.value}
                      getItemTitleByValue={(q) => q?.name ?? ''}
                      equalityFn={(a, b) => a.id === b.id}
                      disabled={isLoadingQueues || disabled}
                      placeholder={
                        isLoadingQueues
                          ? 'Loading available queues...'
                          : 'Select queues'
                      }
                      filter={{
                        name: 'searchQueuesInput',
                        value: queueSearchValue,
                        onChange: (e) => setQueueSearchValue(e.target.value),
                      }}
                      selectionListProps={{
                        style: { maxHeight: 320 },
                      }}
                      onChange={({ selectedValues }) =>
                        field.onChange(selectedValues)
                      }
                    >
                      {data.map((queue) => (
                        <SelectionList.Item key={queue.id} value={queue}>
                          <Text.Paragraph>{queue.name}</Text.Paragraph>
                        </SelectionList.Item>
                      ))}
                      {isFetching && (
                        <FlexBox
                          alignItems="center"
                          justifyContent="center"
                          padding="400 200"
                        >
                          <Spinner size={24} />
                        </FlexBox>
                      )}
                      {!isFetching && !error && hasNextPage && (
                        <Waypoint onEnter={nextPage} />
                      )}
                    </InputSelectionList>
                  </FieldWrapper>
                )}
              />
            </FormField>
          </>
        )}
        <FormField>
          <FieldTogglePanel
            control={control}
            name="internalKbEnabled"
            heading="Show in internal knowledge base"
            noAlert={true}
            disabled={disabled}
          />
        </FormField>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button variant="tertiary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            disabled={isFetching || disabled || !isValid}
            onClick={onSave}
          >
            Save
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
};

type CollectionDetachBannerProps = {
  collectionId: string;
  isSyncedWithElevio: boolean;
};
export function CollectionDetachBanner({
  collectionId,
  isSyncedWithElevio,
}: CollectionDetachBannerProps): JSX.Element | null {
  const { showModal } = useModal();

  if (!isSyncedWithElevio) return null;

  function onUnlock() {
    // This will dismiss the existing modal and invoke a confirmation modal to detach the collection
    showModal(CollectionDetachModal, { collectionId });
  }

  return (
    <Box margin="0 0 300">
      <DixaBanner
        description={
          <>
            <Text.Paragraph variant="4">
              This collection was imported from Elevio.
            </Text.Paragraph>
            <Text.Paragraph variant="4">
              Detach the collection to make changes to content such as
              reordering or moving articles and categories.
            </Text.Paragraph>
          </>
        }
        variant="info"
        actionSlot={
          <Button variant="secondary" onClick={onUnlock}>
            Detach collection
          </Button>
        }
      />
    </Box>
  );
}
